import { BLACKLISTED_DOMAINS_MAX_COUNT } from '@va/constants';
import { useGetBlacklistedDomains } from '@va/dashboard/api-client/Website';
import { useAddNotification, useFormError } from '@va/dashboard/util-hooks';
import { validateDomain } from '@va/util/helpers';
import { isEqual } from 'lodash';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useUpdateBlacklistedDomains } from '../apiClient';

export default function useDomainBlacklistingCard() {
  const [inputValue, setInputValue] = useState<string>('');
  const [successSubmit, setSuccessSubmit] = useState(false);
  const { formError, handleFormError } = useFormError();
  const [blacklistedDomains, setBlacklistedDomains] = useState<string[]>([]);

  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const { data: existingDomains, isLoading: isLoadingExisting, mutate: refreshDomains } = useGetBlacklistedDomains();
  const {
    execute: updateDomains,
    isLoading: isUpdating,
    isSucceeded: isUpdateSuccess,
    error: updateError,
  } = useUpdateBlacklistedDomains();

  const hasMadeChanges = useMemo(() => {
    return !isLoadingExisting && blacklistedDomains && !isEqual(existingDomains?.payload, blacklistedDomains);
  }, [isLoadingExisting, blacklistedDomains, existingDomains]);

  useEffect(() => {
    if (existingDomains) {
      setBlacklistedDomains(existingDomains.payload);
    }
  }, [existingDomains]);

  useEffect(() => {
    if (isUpdateSuccess) {
      showSuccessNotification(2000);
      refreshDomains();
    }
    if (updateError) {
      showErrorNotification(2000);
      refreshDomains();
    }
  }, [existingDomains, updateError, isUpdateSuccess, showErrorNotification, showSuccessNotification, refreshDomains]);

  const onChange = (value: string) => setInputValue(value);

  function handleSuccessSubmit() {
    setInputValue('');
    setSuccessSubmit(true);
    setTimeout(() => {
      setSuccessSubmit(false);
    }, 500);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();

    if (!validateDomain(inputValue)) {
      return handleFormError('card.blackListing.blacklistingDomainValidationMessage');
    }

    if (blacklistedDomains.includes(inputValue)) {
      return handleFormError('card.blackListing.alreadyAddedDomainMessage');
    }

    if (blacklistedDomains.length >= BLACKLISTED_DOMAINS_MAX_COUNT) {
      return handleFormError('card.blackListing.error.maxBlacklistLimitReached');
    }

    setBlacklistedDomains((prev) => [...prev, inputValue]);
    handleSuccessSubmit();
  }

  const onRemove = (value: string) => setBlacklistedDomains((prev) => prev.filter((domain) => domain !== value));
  const onSave = async () => {
    const addedDomains = blacklistedDomains.filter((domain) => !existingDomains?.payload.includes(domain));
    const deletedDomains = existingDomains?.payload.filter((domain) => !blacklistedDomains?.includes(domain)) ?? [];
    await updateDomains(addedDomains, deletedDomains);
  };

  return {
    blacklistedDomains,
    inputValue,
    formError,
    isLoading: isLoadingExisting,
    isLoadingUpdateCall: isUpdating,
    hasMadeChanges,
    successSubmit,
    onRemove,
    onSubmit,
    onChange,
    onSave,
  };
}
